import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Button,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WatchLaterRoundedIcon from "@mui/icons-material/WatchLaterRounded";
import TestingImg from "../../common/assests/gridimg3.png";
import Paginations from "../../common/pagination/Paginations";
import { useSelector } from "react-redux";
import {
  allVenderEventsApi,
  blockVenderEventApi,
  deleteVenderEventApi,
  resumeVenderEventApi,
} from "../../api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import configuration from "../../config/Config";
import { AdminTable, ActionCell } from "../../common/adminTable";

const CreatedEvent = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const { id } = useParams();
  const [eventData, setEventData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState();
  const [change, setChange] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState();

  const handleAllEventsData = async () => {
    try {
      setLoading(true);
      const response = await allVenderEventsApi({ token, currentPage, id });
      setEventData(response?.events || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleBlock = async (id) => {
    if (!await confirm({ title: "Unpublish Event", description: "Are you sure you want to unpublish this event?", confirmText: "Unpublish" })) return;
    try {
      const response = await blockVenderEventApi({ token, id });
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occured" : error?.error);
      console.log("error", error);
    }
  };

  const handleResume = async (id) => {
    if (!await confirm({ title: "Publish Event", description: "Are you sure you want to publish this event?", confirmText: "Publish" })) return;
    try {
      const response = await resumeVenderEventApi({ token, id });
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occured" : error?.error);
      console.log("error", error);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: "Delete Event", description: "Are you sure you want to delete this event?", confirmText: "Delete", destructive: true })) return;
    try {
      const response = await deleteVenderEventApi({ token, id });
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occured" : error?.error);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleAllEventsData();
  }, [currentPage, change]);

  const handleOpen = (event) => {
    setSelectedEvent(event);
    setOpen(true);
  };

  const isPublished = selectedEvent?.status === "active";
  const handleClose = () => setOpen(false);

  const handlePageChange = (page) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const columns = [
    {
      field: "picture",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={row?.picture ? `${configuration.cdnFront}/${row.picture}` : TestingImg}
          alt="event"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Title", minWidth: 140, renderCell: (r) => r?.title || "-" },
    {
      field: "eventDate",
      headerName: "Event Date",
      width: 120,
      renderCell: (r) =>
        r?.eventDate
          ? new Date(r.eventDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
          : "-",
    },
    { field: "cTag", headerName: "Category", width: 120, renderCell: (r) => r?.cTag || "-" },
    {
      field: "description",
      headerName: "Description",
      minWidth: 180,
      cellSx: { maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (r) => r?.description || "-",
    },
    { field: "status", headerName: "Status", width: 90, renderCell: (r) => r?.status || "-" },
    {
      field: "__actions",
      headerName: "",
      width: 80,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleOpen(row),
            },
            {
              label: "Delete",
              icon: <DeleteOutlineIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => handleDelete(row?._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ mt: "40px", mb: "30px" }}>
      <Typography sx={{ fontSize: "20px", fontWeight: "500", color: "#362B24", fontFamily: "Heebo", mb: 2 }}>
        All Created Events
      </Typography>
      <AdminTable
        columns={columns}
        rows={eventData}
        loading={loading}
        rowKey="_id"
        emptyText="No events found"
      />
      <Paginations
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        disabled={loading}
      />

      {/* Event Detail Modal */}
      {selectedEvent && (
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "80vw", sm: "80vw" },
              maxWidth: "630px",
              bgcolor: "#FFF",
              borderRadius: "32px",
              boxShadow: 24,
              maxHeight: "80vh",
              p: 4,
              overflowY: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>Event Details</Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <img
              src={selectedEvent?.picture ? `${configuration.cdnFront}/${selectedEvent?.picture}` : TestingImg}
              alt="Event"
              style={{ width: "100%", maxHeight: "263px", borderRadius: "20px", marginTop: "15px" }}
            />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: { xs: "column", sm: "row" }, gap: 2, mt: 3 }}>
              <Typography sx={{ fontSize: "18px", fontWeight: "700", color: "#362B24", fontFamily: "Heebo" }}>
                {selectedEvent?.title || "-"}
              </Typography>
              <Box sx={{ borderRadius: "28px", background: "#362B24", width: "110px", height: "30px", display: "flex", alignItems: "center", padding: "0 8px", flexShrink: 0, gap: 1 }}>
                <WatchLaterRoundedIcon sx={{ color: "#FFF", width: "18px", height: "18px" }} />
                <Typography sx={{ color: "#FFF", fontSize: "10px" }}>
                  {selectedEvent?.eventDate
                    ? new Date(selectedEvent?.eventDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                    : "-"}
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ fontWeight: "400", color: "#362B24", fontSize: "13px", fontFamily: "Heebo", marginTop: "10px" }}>
              Event Category
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
              <LocationOnIcon sx={{ color: "#065F46", fontSize: "16px", flexShrink: 0 }} />
              <Typography sx={{ color: "#065F46", fontSize: "13px", fontWeight: "400", fontFamily: "Heebo" }}>
                {selectedEvent?.address || "-"}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "17px", fontWeight: "600", color: "#362B24", fontFamily: "Heebo", mt: 3 }}>
              Description
            </Typography>
            <Typography sx={{ mb: 2, opacity: 0.5, fontFamily: "Heebo" }}>
              {selectedEvent?.description || "-"}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mt: 3 }}>
              <Button
                onClick={isPublished ? () => handleBlock(selectedEvent?._id) : () => handleResume(selectedEvent?._id)}
                startIcon={isPublished ? <LockIcon /> : <LockOpenIcon />}
                sx={{
                  borderRadius: "12px",
                  backgroundColor: isPublished ? "#F0EEEC" : "#362B24",
                  minWidth: "160px",
                  height: "46px",
                  color: isPublished ? "#362B24" : "#FFF",
                  fontFamily: "Heebo",
                  textTransform: "none",
                  fontWeight: 500,
                  '&:hover': { backgroundColor: isPublished ? "#E0DDDA" : "#2a2018" },
                }}
              >
                {isPublished ? "Unpublish" : "Publish"}
              </Button>
              <Button
                onClick={() => handleDelete(selectedEvent?._id)}
                startIcon={<DeleteIcon />}
                sx={{
                  borderRadius: "12px",
                  backgroundColor: "#EA001B",
                  minWidth: "160px",
                  height: "46px",
                  color: "#FFF",
                  fontFamily: "Heebo",
                  textTransform: "none",
                  fontWeight: 500,
                  '&:hover': { backgroundColor: "#c8001a" },
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default CreatedEvent;
